// src/oura/components/docs/create/ConvertWordSheet.tsx
//
// "Convert Word files" (#4328, ourteam-backend#6088): every Word file in the
// client's folder that has no Google Doc yet, converted in one action. The
// sheet reads the server's plan (what each file becomes, read from its name),
// lists the ones that need a manual convert with the reason, and converts the
// rest a few files a call, since each is a Drive copy, saying how far it got.
// Each file answers for itself: one refused never stops the others. The board
// reads itself again on its own once a convert succeeds.

import { useEffect, useState } from "react";
import { FileTextIcon } from "lucide-react";
import { convertWordFiles, getConvertWordPlan, type ConvertWordItem, type ConvertWordPlan, type ConvertWordResult } from "@service/aeo/clientDocsWrite";
import type { DocsFailure } from "@service/aeo/clientDocs";
import { DrawerBlock, DrawerFoot, DrawerHead, PillButton, TYPE, type DrawerFootMessage } from "@oura/ui";
import { docsFailureText } from "@oura/docs";
import { cn } from "../../../lib/cn";

/** Files per call: each is a Drive copy of a few seconds, and a call must answer inside its timeout. */
const PER_CALL = 3;

function becomes(item: ConvertWordItem): string {
    const codes = item.codes.length ? ` (${item.codes.join(", ")})` : "";
    const type = item.docType ? `${item.docType}${item.custom ? " (custom doc type)" : ""}` : "Unknown";
    return `${type}${codes}${item.outlet ? `, ${item.outlet}` : ""}`;
}

export function ConvertWordSheet({ businessId, onClose, onBusy }: { businessId: string; onClose: () => void; onBusy?: (busy: boolean) => void }) {
    const [plan, setPlan] = useState<ConvertWordPlan | null>(null);
    const [failure, setFailure] = useState<DocsFailure | null>(null);
    const [running, setRunning] = useState<{ done: number; of: number } | null>(null);
    const [results, setResults] = useState<Map<string, ConvertWordResult> | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);

    useEffect(() => {
        let alive = true;
        void getConvertWordPlan(businessId).then((r) => {
            if (!alive) return;
            if (r.ok) setPlan(r.data);
            else setFailure(r);
        });
        return () => {
            alive = false;
        };
    }, [businessId]);

    const ready = (plan?.items ?? []).filter((i) => i.ready);
    const manual = (plan?.items ?? []).filter((i) => !i.ready);

    const run = async () => {
        if (running || ready.length === 0) return;
        const ids = ready.map((i) => i.documentId);
        const got = new Map<string, ConvertWordResult>();
        onBusy?.(true);
        setMessage(null);
        setRunning({ done: 0, of: ids.length });
        for (let at = 0; at < ids.length; at += PER_CALL) {
            const chunk = ids.slice(at, at + PER_CALL);
            const r = await convertWordFiles(businessId, chunk);
            if (r.ok) {
                for (const res of r.data.results) got.set(res.documentId, res);
            } else {
                // The call itself failed: these files are reported as not converted, the rest still go.
                for (const id of chunk) got.set(id, { documentId: id, ok: false, message: docsFailureText(r, "this file") });
            }
            setRunning({ done: Math.min(at + chunk.length, ids.length), of: ids.length });
        }
        const converted = Array.from(got.values()).filter((x) => x.ok).length;
        setResults(got);
        setRunning(null);
        onBusy?.(false);
        setMessage(
            converted === ids.length
                ? { tone: "success", text: `Converted ${converted} Word ${converted === 1 ? "file" : "files"} to Google Docs.` }
                : { tone: "error", text: `Converted ${converted} of ${ids.length}. The others say why below.` },
        );
    };

    const finished = results !== null;
    const runLabel = `Convert ${ready.length} ${ready.length === 1 ? "file" : "files"}`;

    return (
        <>
            <DrawerHead title="Convert Word files" />
            {failure ? (
                <DrawerBlock>
                    <p className="m-0 text-gray-700">{docsFailureText(failure, "the Word files")}</p>
                </DrawerBlock>
            ) : !plan ? (
                <DrawerBlock busy>
                    <p className="m-0 text-gray-500">Reading the Word files…</p>
                </DrawerBlock>
            ) : plan.items.length === 0 ? (
                <DrawerBlock>
                    <p className="m-0 text-gray-700">Every Word file in this folder has a Google Doc already.</p>
                </DrawerBlock>
            ) : (
                <>
                    <DrawerBlock label={`Ready to convert · ${ready.length}`}>
                        {ready.length === 0 ? (
                            <p className="m-0 text-gray-500">None. Each file below needs a manual convert.</p>
                        ) : (
                            <ul className="m-0 list-plain space-y-2 p-0">
                                {ready.map((i) => {
                                    const res = results?.get(i.documentId);
                                    return (
                                        <li key={i.documentId} className="flex items-start gap-2">
                                            <FileTextIcon aria-hidden size={14} className="mt-[3px] shrink-0 text-blue-600" />
                                            <span className="min-w-0 break-words">
                                                <span className="font-medium text-gray-900">{i.name}</span>
                                                <span className={cn(TYPE.tc, "block text-gray-500")}>Becomes: {becomes(i)}</span>
                                                {res && (
                                                    <span className={cn(TYPE.tc, "block font-medium", res.ok ? "text-emerald-700" : "text-rose-700")}>
                                                        {res.ok ? "Converted" : res.message ?? "Not converted"}
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </DrawerBlock>
                    {manual.length > 0 && (
                        <DrawerBlock label={`Needs a manual convert · ${manual.length}`}>
                            <ul className="m-0 list-plain space-y-2 p-0">
                                {manual.map((i) => (
                                    <li key={i.documentId} className="min-w-0 break-words">
                                        <span className="font-medium text-gray-900">{i.name}</span>
                                        <span className={cn(TYPE.tc, "block text-gray-500")}>{i.reason}</span>
                                    </li>
                                ))}
                            </ul>
                        </DrawerBlock>
                    )}
                    {running && (
                        <DrawerBlock busy>
                            <p className="m-0 text-gray-700">
                                Converting {running.done} of {running.of}…
                            </p>
                        </DrawerBlock>
                    )}
                </>
            )}
            <DrawerFoot
                message={message}
                first={
                    !finished && ready.length > 0 && !failure ? (
                        <PillButton variant="primary" busy={Boolean(running)} disabled={Boolean(running)} onClick={() => void run()}>
                            {runLabel}
                        </PillButton>
                    ) : undefined
                }
                second={
                    <PillButton variant="text" disabled={Boolean(running)} onClick={onClose}>
                        {finished ? "Done" : "Cancel"}
                    </PillButton>
                }
            />
        </>
    );
}
